import { Emitter, type EmitterLike } from "@infra-blocks/emitter";
import type { ErrorHandler } from "@infra-blocks/types";

/**
A custom proxy function to fetch that handles known request headers.
*/
export async function http(...args: Parameters<typeof fetch>) {
  const response = await fetch(...args);
  return response;
}

export type HttpEvents = {
  /**
  This event is fired immediately after the request is sent, before the
  response is received.
  */
  "pre:response": () => void;
  /**
  This event is fired last, after `error` if an occurred, and after `response`
  if a response was available.

  This is literally fired in a finally block.
  */
  "post:response": () => void;
  /**
  Convenience event fired when the response is received.

  Subscribing to this is almost equivalent to awaiting the promise returned by
  `HttpEmitter`. The difference being that post:response will have fired
  before the promise is returned.
  */
  response: (res: Response) => void;
  /**
  Event emitted when the request could not be sent, or could not reach
  the server.
  */
  error: ErrorHandler;
};

export type HttpEmitter = typeof http & EmitterLike<HttpEvents>;

export function httpEmitter(): HttpEmitter {
  const emitter = Emitter.ignoringEach<HttpEvents>();
  const wrapper = async (...args: Parameters<typeof http>) => {
    try {
      const promise = http(...args);
      emitter.emit("pre:response");
      const response = await promise;
      emitter.emit("response", response);
      return response;
    } catch (err) {
      emitter.emit("error", err);
    } finally {
      emitter.emit("post:response");
    }
  };
  wrapper.on = (...args: Parameters<typeof emitter.on>) => {
    emitter.on(...args);
    return wrapper;
  };
  wrapper.once = (...args: Parameters<typeof emitter.once>) => {
    emitter.once(...args);
    return wrapper;
  };

  return wrapper as HttpEmitter;
}
